import CustomChip from 'Components/CustomChip'
import type {
  WalletTransaction,
  WebShopOrderLineShape
} from 'Services/SalesManagement/Features/Order/orderSlice'
import { showValueOrHyphen } from 'Util/formatters'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { CircularProgress, Collapse, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { type FC, useState } from 'react'
import { t } from 'ttag'

const WebShopOrderLineRow: FC<{
  orderId: number
  row: WebShopOrderLineShape
  handleOpenLogClick: (id: number, line_pk: number) => void
  isLogLoading: boolean
  handleWalletTransactionRefundClick: (id: string) => void
  isWalletTransactionRefundLoading: boolean
}> = ({
  orderId,
  row,
  handleOpenLogClick,
  isLogLoading,
  handleWalletTransactionRefundClick,
  isWalletTransactionRefundLoading
}) => {
  const [open, setOpen] = useState(false)

  const getTransactionAction = (transaction: WalletTransaction | null) => {
    if (transaction == null) {
      return '-'
    }
    return transaction.rolled_back ? (
      t`Refunded`
    ) : (
      <CustomChip
        onClick={() => handleWalletTransactionRefundClick(transaction.id)}
        label={
          isWalletTransactionRefundLoading ? (
            <CircularProgress sx={{ display: 'block' }} size={16} />
          ) : (
            t`Refund`
          )
        }
      />
    )
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton aria-label={t`Expand row`} size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.travel_card_number}</TableCell>
        <TableCell>{row.product_name}</TableCell>
        <TableCell>{row.status}</TableCell>
        <TableCell>{showValueOrHyphen(row.delivery_message)}</TableCell>
        <TableCell>
          <CustomChip
            onClick={() => handleOpenLogClick(orderId, row.id)}
            label={
              isLogLoading ? <CircularProgress sx={{ display: 'block' }} size={16} /> : t`Show log`
            }
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }} component="div">
                {t`Price info`}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t`Price without VAT`}</TableCell>
                    <TableCell>{t`VAT`}</TableCell>
                    <TableCell>{t`VAT percent`}</TableCell>
                    <TableCell>{t`Discount percent`}</TableCell>
                    <TableCell>{t`Discount codes`}</TableCell>
                    <TableCell align="right">{t`Total`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{row.vat_exclusive_price} €</TableCell>
                    <TableCell>{row.vat_amount} €</TableCell>
                    <TableCell>{row.vat_percentage} %</TableCell>
                    <TableCell>{showValueOrHyphen(row.discount_percent, '%')}</TableCell>
                    <TableCell>
                      {row.discount_codes.length > 0 ? row.discount_codes.toString() : '-'}
                    </TableCell>
                    <TableCell align="right">{row.amount} €</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }} component="div">
                {t`Benefits`}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }} component="div">
                {t`Product benefits`}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t`Description`}</TableCell>
                    <TableCell>{t`Status`}</TableCell>
                    <TableCell>{t`Amount`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(row.product_benefit).length > 0 ? (
                    <TableRow>
                      <TableCell>{row.product_benefit.description}</TableCell>
                      <TableCell>{row.product_benefit.reservation_status}</TableCell>
                      <TableCell>{row.product_benefit.applied_amount} €</TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3}>{t`No product benefits applied`}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" sx={{ mt: 1 }} component="div">
                {t`Wallet benefits`}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t`Transaction ID`}</TableCell>
                    <TableCell>{t`Amount`}</TableCell>
                    <TableCell>{t`Refund`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(row.wallet_benefit).length > 0 ? (
                    <TableRow>
                      <TableCell>{showValueOrHyphen(row.wallet_benefit.transaction?.id)}</TableCell>
                      <TableCell>{row.wallet_benefit.applied_amount} €</TableCell>
                      <TableCell>{getTransactionAction(row.wallet_benefit.transaction)}</TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3}>{t`No wallet benefits applied`}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export { WebShopOrderLineRow }
